// Per-discipline OGS editor.
//
// Scenario-driven OGS: each discipline can enable a combined "OGS" bucket,
// pick the hub that staffs it, set a 0-100% share, and choose which level
// groups the share applies to. Multi-channel disciplines (e.g. Investment)
// expose one independent line per channel so each channel can be tuned on its
// own; single-channel disciplines show a single line. The model's hubPct table
// only seeds sensible defaults (via deriveDefaultOgsForDiscipline); the values
// here drive the calculation. Disabled lines are 100% onshore.

import clsx from 'clsx';
import {
  LEVEL_GROUPS,
  deriveDefaultOgsForDiscipline,
  groupsForLevels,
  isOgsEligible,
  levelsForGroups,
  type LevelGroupId,
} from '@rc/engine';
import type { Discipline, Model, OgsDisciplineConfig, OgsHub } from '@rc/engine/types';

interface Props {
  discipline: Discipline;
  model: Model;
  configs: OgsDisciplineConfig[];
  onChange: (next: OgsDisciplineConfig) => void;
}

function hubLabel(h: OgsHub): string {
  const shore = h.shore === 'nearshore' ? 'Nearshore' : 'Offshore';
  return `${h.label} · ${shore}`;
}

/** Distinct channels the engine will produce cells for (from spendPerFte). */
function channelsForDiscipline(
  model: Model,
  disciplineId: string,
): { id: string; label: string }[] {
  const ids = Array.from(
    new Set(
      (model.spendPerFte || [])
        .filter((r) => r.disciplineId === disciplineId)
        .map((r) => r.channelId),
    ),
  );
  if (ids.length === 0) ids.push('all');
  return ids.map((id) => ({
    id,
    label:
      id === 'all'
        ? 'All channels'
        : (model.channels || []).find((c) => c.id === id)?.label || id,
  }));
}

function sameChannel(a: string | undefined, b: string | undefined): boolean {
  return (a ?? 'all') === (b ?? 'all');
}

export default function OgsDisciplineRow({
  discipline,
  model,
  configs,
  onChange,
}: Props) {
  const channels = channelsForDiscipline(model, discipline.id).filter((ch) =>
    isOgsEligible(model, discipline.id, ch.id),
  );
  // All channels toggled off in the model → this discipline offers no OGS.
  if (channels.length === 0) return null;
  const multiChannel = channels.length > 1;

  const configFor = (channelId: string): OgsDisciplineConfig | undefined =>
    configs.find(
      (c) => c.disciplineId === discipline.id && sameChannel(c.channelId, channelId),
    );

  if (!multiChannel) {
    const ch = channels[0];
    return (
      <OgsChannelLine
        label={discipline.label}
        labelWidth="w-40"
        variant="card"
        disciplineId={discipline.id}
        channelId={ch.id}
        model={model}
        value={configFor(ch.id)}
        onChange={onChange}
      />
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <div className="text-sm font-medium text-slate-800 mb-2">
        {discipline.label}
        <span className="ml-2 text-[11px] font-normal text-slate-400">
          per channel
        </span>
      </div>
      <div className="space-y-1.5">
        {channels.map((ch) => (
          <OgsChannelLine
            key={ch.id}
            label={ch.label}
            labelWidth="w-32"
            variant="row"
            disciplineId={discipline.id}
            channelId={ch.id}
            model={model}
            value={configFor(ch.id)}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A single compact OGS line for one (discipline, channel).
// ---------------------------------------------------------------------------
function OgsChannelLine({
  label,
  labelWidth,
  variant,
  disciplineId,
  channelId,
  model,
  value,
  onChange,
}: {
  label: string;
  labelWidth: string;
  variant: 'card' | 'row';
  disciplineId: string;
  channelId: string;
  model: Model;
  value: OgsDisciplineConfig | undefined;
  onChange: (next: OgsDisciplineConfig) => void;
}) {
  const hubs = model.ogsHubs || [];
  const enabled = !!value?.enabled;
  const hubId = value?.hubId;
  const pct = Number(value?.pct) || 0;
  const selectedLevels = value?.levels || [];

  const seededConfig = (nextHubId: string | undefined): OgsDisciplineConfig => {
    const def = deriveDefaultOgsForDiscipline(model, disciplineId, nextHubId, channelId);
    return {
      disciplineId,
      channelId,
      enabled: true,
      hubId: nextHubId,
      pct: def.pct,
      levels: def.levels,
    };
  };

  const handleToggle = (on: boolean) => {
    if (on) {
      if (value) {
        const nextHub = value.hubId || hubs[0]?.id;
        if (!value.hubId && nextHub) onChange(seededConfig(nextHub));
        else onChange({ ...value, channelId, enabled: true });
      } else {
        onChange(seededConfig(hubs[0]?.id));
      }
    } else {
      onChange({ disciplineId, channelId, enabled: false, hubId, pct, levels: selectedLevels });
    }
  };

  const handleHubChange = (nextHubId: string | undefined) => {
    onChange(seededConfig(nextHubId));
  };

  const handlePctChange = (raw: string) => {
    const n = Math.min(100, Math.max(0, Number(raw) || 0));
    onChange({ disciplineId, channelId, enabled: true, hubId, pct: n / 100, levels: selectedLevels });
  };

  const selectedGroups = groupsForLevels(selectedLevels);

  const toggleGroup = (groupId: LevelGroupId, on: boolean) => {
    const next = on
      ? [...selectedGroups, groupId]
      : selectedGroups.filter((g) => g !== groupId);
    onChange({
      disciplineId,
      channelId,
      enabled: true,
      hubId,
      pct,
      levels: levelsForGroups(next),
    });
  };

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-x-4 gap-y-2',
        variant === 'card'
          ? clsx(
              'rounded-lg border px-3 py-2',
              enabled ? 'border-brand-purple-300 bg-brand-purple-50/30' : 'border-slate-200',
            )
          : clsx('rounded-md px-2 py-1', enabled && 'bg-brand-purple-50/40'),
      )}
    >
      <label className={clsx('inline-flex items-center gap-2 cursor-pointer shrink-0', labelWidth)}>
        <input
          type="checkbox"
          className="rounded border-slate-300 text-brand-purple-600"
          checked={enabled}
          onChange={(e) => handleToggle(e.target.checked)}
        />
        <span
          className={clsx(
            'truncate',
            variant === 'card'
              ? 'text-sm font-medium text-slate-800'
              : 'text-xs text-slate-600',
          )}
        >
          {label}
        </span>
      </label>

      <div
        className={clsx(
          'flex flex-wrap items-center gap-x-4 gap-y-2 flex-1',
          !enabled && 'opacity-50 pointer-events-none select-none',
        )}
        aria-disabled={!enabled}
      >
        <select
          className="text-xs border border-slate-300 rounded px-1.5 py-1 bg-white outline-none focus:border-brand-purple-400"
          value={hubId || ''}
          disabled={!enabled}
          onChange={(e) => handleHubChange(e.target.value || undefined)}
          title="OGS hub location"
        >
          <option value="">Hub…</option>
          {hubs.map((h) => (
            <option key={h.id} value={h.id}>
              {hubLabel(h)}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(pct * 100)}
            disabled={!enabled}
            onChange={(e) => handlePctChange(e.target.value)}
            className="w-24 accent-brand-purple-600"
            title="OGS % (hub share)"
          />
          <div className="relative w-14">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={Math.round(pct * 100)}
              disabled={!enabled}
              onChange={(e) => handlePctChange(e.target.value)}
              className="input py-0.5 pl-1.5 pr-4 w-full text-right text-xs font-semibold text-brand-purple-700 tabular-nums"
              title="OGS % (hub share)"
            />
            <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
              %
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {LEVEL_GROUPS.map((g) => {
            const on = selectedGroups.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                disabled={!enabled}
                onClick={() => toggleGroup(g.id, !on)}
                title={g.label}
                className={clsx(
                  'rounded border px-1.5 py-0.5 text-[11px] font-medium transition-colors',
                  on
                    ? 'bg-brand-purple-100 text-brand-purple-700 border-brand-purple-300'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300',
                )}
              >
                {g.name}
              </button>
            );
          })}
          {enabled && selectedLevels.length === 0 && (
            <span className="text-[11px] text-amber-700 ml-1">no roles → no OGS</span>
          )}
        </div>
      </div>
    </div>
  );
}
